<!-- ═══ Payment success banner ══════════════════════════════════════════════ -->
@if (paymentSuccess) {
  <div
    class="rounded-xl p-5 mx-4 mt-4 md:mx-auto md:max-w-3xl flex items-start gap-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-300"
  >
    <span class="text-3xl leading-none mt-0.5">🎉</span>
    <div>
      <h3 class="font-bold text-emerald-900 dark:text-emerald-200 text-lg">
        Paiement confirmé — votre abonnement est actif !
      </h3>
      <p class="text-emerald-800 dark:text-emerald-300 mt-1">
        Vous avez désormais accès à toutes les fonctionnalités de votre plan.
      </p>
    </div>
  </div>
}

<!-- ═══ Plan change success banner ═════════════════════════════════════════ -->
@if (planChangeSuccess) {
  <div
    class="rounded-xl p-5 mx-4 mt-4 md:mx-auto md:max-w-3xl flex items-start gap-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-300"
  >
    <span class="text-3xl leading-none mt-0.5">✅</span>
    <div>
      <h3 class="font-bold text-emerald-900 dark:text-emerald-200 text-lg">
        Changement de plan confirmé !
      </h3>
      <p class="text-emerald-800 dark:text-emerald-300 mt-1">
        {{ planChangeMessage }}
      </p>
    </div>
  </div>
}

<!-- ═══ Payment loading ════════════════════════════════════════════════════ -->
@if (paymentLoading) {
  <div class="flex flex-col items-center justify-center py-20">
    <div class="spinner w-8 h-8 border-purple-600 mb-4"></div>
    <p class="text-gray-500 dark:text-gray-400">
      Vérification du paiement en cours…
    </p>
  </div>
}

<!-- ═══ Trial expired banner ═══════════════════════════════════════════════ -->
@if (trialExpired) {
  <div
    class="trial-banner rounded-xl p-5 mx-4 mt-4 md:mx-auto md:max-w-3xl flex items-start gap-3"
  >
    <span class="text-3xl leading-none mt-0.5">⏳</span>
    <div>
      <h3 class="font-bold text-amber-900 dark:text-amber-200 text-lg">
        Votre période d'essai gratuite d'1 mois est terminée
      </h3>
      <p class="text-amber-800 dark:text-amber-300 mt-1">
        Pour continuer à utiliser Predivent et accéder à vos données, choisissez
        un abonnement ci-dessous.
      </p>
      @if (recommendedPlan !== "pro") {
        <p class="text-amber-700 dark:text-amber-300 text-sm mt-2">
          💡 Vous aviez choisi le plan
          <span class="font-bold capitalize">{{ recommendedPlan }}</span> lors
          de votre inscription.
        </p>
      }
    </div>
  </div>
}

<!-- ═══ Payment failed banner (redirecté par interceptor) ═══════════════════════════ -->
@if (isPaused || pausedRedirect) {
  <div
    class="rounded-xl p-5 mx-4 mt-4 md:mx-auto md:max-w-3xl flex items-start gap-3 bg-sky-50 dark:bg-sky-900/20 border border-sky-300"
  >
    <span class="text-3xl leading-none mt-0.5">⏸️</span>
    <div class="flex-1">
      <h3 class="font-bold text-sky-900 dark:text-sky-200 text-lg">
        Votre abonnement est en pause
      </h3>
      <p class="text-sky-800 dark:text-sky-300 mt-1">
        Votre historique et vos données personnelles sont conservés. Vous pouvez
        reprendre votre abonnement à tout moment (paiement requis, sans nouvelle
        période d'essai gratuite).
      </p>
      <button
        (click)="resumeSubscription()"
        [disabled]="isPortalLoading"
        class="mt-3 inline-flex items-center gap-2 btn-primary px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
      >
        ▶️ Reprendre mon abonnement
        @if (subscription()?.pausedPlan) {
          <span class="uppercase">({{ subscription()?.pausedPlan }})</span>
        }
      </button>
    </div>
  </div>
}

@if (paymentFailed || isSubscriptionBlocked) {
  <div
    class="rounded-xl p-5 mx-4 mt-4 md:mx-auto md:max-w-3xl flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-300"
  >
    <span class="text-3xl leading-none mt-0.5">🚨</span>
    <div class="flex-1">
      <h3 class="font-bold text-red-900 dark:text-red-300 text-lg">
        Paiement échoué — accès bloqué
      </h3>
      <p class="text-red-800 dark:text-red-300 mt-1">
        Votre dernier paiement n'a pas pu être traité. Vous pouvez renouveler
        directement votre abonnement ou mettre à jour votre moyen de paiement.
      </p>
      <div class="mt-3 flex flex-wrap gap-2">
        <button
          (click)="renewBlockedSubscription()"
          [disabled]="isPortalLoading"
          class="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          🔄 Renouveler mon abonnement
        </button>
        <button
          (click)="openStripePortal()"
          class="inline-flex items-center gap-2 bg-white dark:bg-slate-800 border border-red-300 text-red-700 dark:text-red-300 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
        >
          💳 Gérer mon moyen de paiement
        </button>
      </div>
    </div>
  </div>
}

<!-- ═══ Past due warning banner ═══════════════════════════════════════════ -->
@if (isPastDue && !paymentFailed) {
  <div
    class="rounded-xl p-5 mx-4 mt-4 md:mx-auto md:max-w-3xl flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300"
  >
    <span class="text-3xl leading-none mt-0.5">⚠️</span>
    <div>
      <h3 class="font-bold text-amber-900 dark:text-amber-200 text-lg">
        Problème de paiement
      </h3>
      <p class="text-amber-800 dark:text-amber-300 mt-1">
        Votre dernier paiement n’a pas abouti. Stripe réessaiera
        automatiquement, mais nous vous recommandons de vérifier votre moyen de
        paiement.
      </p>
      <button
        (click)="openStripePortal()"
        class="mt-3 inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-700 transition-colors"
      >
        💳 Vérifier mon moyen de paiement
      </button>
    </div>
  </div>
}

@if (!paymentLoading) {
  <div class="bg-gray-50 dark:bg-slate-900 min-h-screen pb-20 pt-6">
    <div class="max-w-6xl mx-auto px-4">
      <!-- ═══ Current plan status card ══════════════════════════════════════ -->
      @if (subscription() && !trialExpired) {
        <div
          class="status-card rounded-2xl p-6 md:p-8 mb-10 shadow-sm dark:shadow-slate-900/30"
        >
          <div
            class="flex flex-col md:flex-row items-start md:items-center justify-between gap-5"
          >
            <div>
              <p
                class="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1"
              >
                Plan actuel
              </p>
              <div class="flex items-center gap-3">
                <span
                  class="text-2xl font-extrabold text-gray-900 dark:text-white uppercase"
                >
                  {{ subscription()?.plan }}
                </span>

                <!-- Badge: payant actif -->
                @if (
                  !isFree &&
                  subscription()?.status === "active" &&
                  !subscription()?.cancelAtPeriodEnd
                ) {
                  <span
                    class="inline-flex items-center gap-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 text-xs px-2.5 py-1 rounded-full font-semibold"
                  >
                    <span
                      class="w-1.5 h-1.5 bg-emerald-500 rounded-full"
                    ></span>
                    Actif
                  </span>
                }
                <!-- Badge: résiliation programmée -->
                @if (subscription()?.cancelAtPeriodEnd && !isPauseScheduled) {
                  <span
                    class="inline-flex items-center gap-1 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-xs px-2.5 py-1 rounded-full font-semibold"
                  >
                    <span class="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                    Résiliation programmée
                  </span>
                }
                <!-- Badge: pause programmée / effective -->
                @if (isPauseScheduled || isPaused) {
                  <span
                    class="inline-flex items-center gap-1 bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300 text-xs px-2.5 py-1 rounded-full font-semibold"
                  >
                    <span class="w-1.5 h-1.5 bg-sky-500 rounded-full"></span>
                    {{ isPaused ? "En pause" : "Pause programmée" }}
                  </span>
                }
                <!-- Badge: downgrade programmé -->
                @if (
                  subscription()?.pendingPlan &&
                  !subscription()?.cancelAtPeriodEnd
                ) {
                  <span
                    class="inline-flex items-center gap-1 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 text-xs px-2.5 py-1 rounded-full font-semibold"
                  >
                    <span class="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                    Changement programmé
                  </span>
                }
                <!-- Badge: période d'essai -->
                @if (isFree && !isPaused) {
                  <span
                    class="inline-flex items-center gap-1 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-xs px-2.5 py-1 rounded-full font-semibold"
                  >
                    <span class="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                    Période d'essai
                  </span>
                }
                <!-- Badge: paiement en retard -->
                @if (isPastDue) {
                  <span
                    class="inline-flex items-center gap-1 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-xs px-2.5 py-1 rounded-full font-semibold"
                  >
                    <span class="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                    Paiement en retard
                  </span>
                }
                <!-- Badge: abonnement bloqué -->
                @if (isSubscriptionBlocked) {
                  <span
                    class="inline-flex items-center gap-1 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs px-2.5 py-1 rounded-full font-semibold"
                  >
                    <span class="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                    Accès bloqué
                  </span>
                }
              </div>

              <!-- Info: résiliation programmée -->
              @if (subscription()?.cancelAtPeriodEnd && !isPauseScheduled) {
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Votre accès prendra fin le
                  <span class="font-semibold">{{
                    subscription()?.endDate | date: "longDate"
                  }}</span
                  >.
                </p>
              }
              <!-- Info: pause programmée -->
              @if (isPauseScheduled) {
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Votre abonnement sera mis en pause le
                  <span class="font-semibold">{{
                    subscription()?.endDate | date: "longDate"
                  }}</span
                  >. Accès complet jusque-là, données conservées.
                </p>
              }
              <!-- Info: pause effective -->
              @if (isPaused) {
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Abonnement
                  <span class="font-semibold uppercase">{{
                    subscription()?.pausedPlan
                  }}</span>
                  en pause depuis le
                  <span class="font-semibold">{{
                    subscription()?.pausedAt | date: "longDate"
                  }}</span
                  >. Historique et données conservés.
                </p>
              }
              <!-- Info: renouvellement automatique (payant actif, SANS downgrade programmé) -->
              @if (
                !isFree &&
                subscription()?.status === "active" &&
                !subscription()?.cancelAtPeriodEnd &&
                !subscription()?.pendingPlan
              ) {
                <div
                  class="mt-3 flex items-start gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2"
                >
                  <span class="text-blue-500 text-sm mt-0.5">🔄</span>
                  <div class="text-sm text-blue-800 dark:text-blue-300">
                    <span class="font-semibold"
                      >Renouvellement automatique</span
                    >
                    le
                    <span class="font-bold">{{
                      subscription()?.endDate | date: "longDate"
                    }}</span>
                    @if (subscription()?.startDate) {
                      <span
                        class="text-blue-600 dark:text-blue-400 text-xs block mt-0.5"
                      >
                        Abonné depuis le
                        {{ subscription()?.startDate | date: "longDate" }}
                      </span>
                    }
                  </div>
                </div>
              }
              <!-- Info: transition de plan (downgrade programmé) -->
              @if (
                !isFree &&
                subscription()?.status === "active" &&
                !subscription()?.cancelAtPeriodEnd &&
                subscription()?.pendingPlan
              ) {
                <div class="mt-3 space-y-2">
                  <!-- Timeline visuelle -->
                  <div
                    class="bg-gradient-to-r from-blue-50 to-orange-50 dark:from-slate-800 dark:to-slate-800 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3"
                  >
                    <div class="flex items-center gap-3 mb-3">
                      <span class="text-lg">📋</span>
                      <span
                        class="text-sm font-semibold text-gray-900 dark:text-white"
                        >Transition de plan en cours</span
                      >
                    </div>
                    <!-- Étape 1: Plan actuel -->
                    <div class="flex items-start gap-3 mb-2">
                      <div class="flex flex-col items-center">
                        <div
                          class="w-3 h-3 bg-emerald-500 rounded-full ring-2 ring-emerald-200"
                        ></div>
                        <div class="w-0.5 h-8 bg-gray-300"></div>
                      </div>
                      <div class="text-sm">
                        <span
                          class="font-bold text-emerald-700 dark:text-emerald-400 uppercase"
                          >{{ subscription()?.plan }}</span
                        >
                        <span class="text-gray-600 dark:text-gray-400">
                          — actif jusqu'au
                        </span>
                        <span
                          class="font-semibold text-gray-900 dark:text-white"
                          >{{
                            subscription()?.pendingPlanEffectiveDate
                              | date: "longDate"
                          }}</span
                        >
                        @if (subscription()?.startDate) {
                          <span class="text-gray-400 text-xs block">
                            Abonné depuis le
                            {{ subscription()?.startDate | date: "longDate" }}
                          </span>
                        }
                      </div>
                    </div>
                    <!-- Étape 2: Plan futur -->
                    <div class="flex items-start gap-3">
                      <div class="flex flex-col items-center">
                        <div
                          class="w-3 h-3 bg-orange-400 rounded-full ring-2 ring-orange-200"
                        ></div>
                      </div>
                      <div class="text-sm">
                        <span
                          class="font-bold text-orange-700 dark:text-orange-300 uppercase"
                          >{{ subscription()?.pendingPlan }}</span
                        >
                        <span class="text-gray-600 dark:text-gray-400">
                          — à partir du
                        </span>
                        <span
                          class="font-semibold text-gray-900 dark:text-white"
                          >{{
                            subscription()?.pendingPlanEffectiveDate
                              | date: "longDate"
                          }}</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              }
              <!-- Info: fin de période d'essai (FREE uniquement) -->
              @if (isFree && !isPaused && trialEndDate) {
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {{
                    isPilot
                      ? "Fin de votre accès pilote gratuit"
                      : "Fin de la période d'essai gratuite (1 mois, non renouvelable)"
                  }}
                  le
                  <span class="font-semibold">{{
                    trialEndDate | date: "longDate"
                  }}</span
                  >.
                </p>
              }
            </div>

            <div class="flex flex-wrap gap-3">
              <!-- Réactivation si annulé -->
              @if (subscription()?.cancelAtPeriodEnd && !isPauseScheduled) {
                <button
                  (click)="reactivateSubscription()"
                  class="btn-primary px-5 py-2.5 rounded-xl text-sm font-semibold"
                >
                  Réactiver mon abonnement
                </button>
              }
              <!-- Annuler la pause programmée / reprendre une pause effective -->
              @if (isPauseScheduled) {
                <button
                  (click)="resumeSubscription()"
                  [disabled]="isPortalLoading"
                  class="btn-primary px-5 py-2.5 rounded-xl text-sm font-semibold"
                >
                  Annuler la pause
                </button>
              }
              @if (isPaused) {
                <button
                  (click)="resumeSubscription()"
                  [disabled]="isPortalLoading"
                  class="btn-primary px-5 py-2.5 rounded-xl text-sm font-semibold"
                >
                  Reprendre mon abonnement
                </button>
              }
              <!-- Factures (légales, tous plans) -->
              <a
                routerLink="/invoices"
                class="btn-outline px-5 py-2.5 rounded-xl text-sm font-semibold"
              >
                Mes factures
              </a>
              <!-- Paiement + Annuler (payant uniquement, pas FREE) -->
              @if (canPause) {
                <button
                  (click)="openStripePortal()"
                  class="btn-outline px-5 py-2.5 rounded-xl text-sm font-semibold"
                >
                  Moyen de paiement
                </button>
                <button
                  (click)="showPauseModal = true"
                  class="btn-outline px-5 py-2.5 rounded-xl text-sm font-semibold"
                >
                  Mettre en pause
                </button>
                <button
                  (click)="openCancelModal()"
                  class="text-red-500 hover:text-red-700 dark:text-red-300 hover:bg-red-50 dark:bg-red-900/20 px-4 py-2.5 rounded-xl text-sm font-medium transition"
                >
                  Annuler
                </button>
              }
            </div>
          </div>
        </div>
      }

      <!-- ═══ Pricing section ═══════════════════════════════════════════════ -->
      <div class="mb-12">
        <div class="text-center mb-10">
          <h2
            class="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white"
          >
            @if (trialExpired) {
              Choisissez votre abonnement
            } @else {
              Changer de plan
            }
          </h2>
          <p class="text-gray-500 dark:text-gray-400 mt-2">
            1 mois d'essai gratuit complet · Sans carte bancaire · Non
            renouvelable · Pause possible après souscription
          </p>

          @if (founderApplies) {
            <div
              class="mt-4 inline-flex flex-col items-center gap-0.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 text-amber-900 dark:text-amber-200 rounded-xl px-5 py-3 text-sm"
            >
              <span class="font-bold"> 🎖️ Tarif fondateur : -30 % à vie </span>
              @if (isFounder) {
                <span>Votre remise fondateur est conservée à la reprise.</span>
              } @else if (isPilot) {
                <span>Une place fondateur vous est réservée.</span>
              } @else {
                <span>
                  {{ founderOffer()?.remaining }} place{{
                    (founderOffer()?.remaining ?? 0) > 1 ? "s" : ""
                  }}
                  restante{{
                    (founderOffer()?.remaining ?? 0) > 1 ? "s" : ""
                  }}
                  sur {{ founderOffer()?.limit }}
                </span>
              }
            </div>
          }

          @if (canChooseInterval) {
            <div
              class="mt-5 inline-flex rounded-full border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-1 text-sm"
              role="group"
              aria-label="Périodicité de facturation"
            >
              <button
                type="button"
                (click)="selectedInterval = 'month'"
                [attr.aria-pressed]="selectedInterval === 'month'"
                class="px-4 py-1.5 rounded-full font-semibold transition"
                [ngClass]="
                  selectedInterval === 'month'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 dark:text-gray-300'
                "
              >
                Mensuel
              </button>
              <button
                type="button"
                (click)="selectedInterval = 'year'"
                [attr.aria-pressed]="selectedInterval === 'year'"
                class="px-4 py-1.5 rounded-full font-semibold transition"
                [ngClass]="
                  selectedInterval === 'year'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 dark:text-gray-300'
                "
              >
                Annuel · 2 mois offerts
              </button>
            </div>
          }
        </div>

        <div
          class="grid grid-cols-1 gap-6 max-w-6xl mx-auto"
          [ngClass]="
            isFree && !isPaused
              ? 'md:grid-cols-4'
              : 'md:grid-cols-3 md:max-w-5xl'
          "
        >
          <!-- ── Free (essai) ─────────────────────────────────────────────── -->
          @if (isFree && !isPaused) {
            <div
              class="pricing-card bg-white dark:bg-slate-800 rounded-2xl p-7 border border-gray-200 dark:border-slate-700 flex flex-col ring-2 ring-purple-500 relative"
            >
              <div
                class="current-badge absolute -top-3 right-4 text-white text-xs font-bold px-3 py-1 rounded-full"
              >
                ACTUEL
              </div>
              <div class="mb-6">
                <h3 class="text-lg font-bold text-gray-900 dark:text-white">
                  Essai gratuit
                </h3>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {{ isPilot ? "Programme pilote" : "Découvrez Predivent" }}
                </p>
              </div>
              <div class="mb-6">
                <span
                  class="text-4xl font-extrabold text-gray-900 dark:text-white"
                  >0€</span
                >
                <span class="text-gray-500 dark:text-gray-400">{{
                  isPilot ? "/accès pilote" : "/1 mois"
                }}</span>
              </div>
              <ul
                class="space-y-3 text-sm text-gray-700 dark:text-gray-300 flex-grow"
              >
                <li class="flex items-center gap-2.5">
                  <span class="check-icon">✓</span> Toutes les fonctionnalités
                  Pro, sans limite d'usage
                </li>
                <li class="flex items-center gap-2.5">
                  <span class="check-icon">✓</span> Recommandations
                  intelligentes
                </li>
                <li class="flex items-center gap-2.5">
                  <span class="check-icon">✓</span> Météo temps réel
                </li>
                <li class="flex items-center gap-2.5">
                  <span class="check-icon">✓</span> Export PDF
                </li>
                <li class="flex items-center gap-2.5">
                  <span class="check-icon">✓</span> Support par email
                </li>
              </ul>
              @if (trialEndDate) {
                <p class="mt-6 text-center text-xs text-gray-400">
                  Expire le {{ trialEndDate | date: "longDate" }}
                </p>
              }
            </div>
          }

          <!-- ── Starter ──────────────────────────────────────────────────── -->
          <div
            class="pricing-card bg-white dark:bg-slate-800 rounded-2xl p-7 border border-gray-200 dark:border-slate-700 flex flex-col"
            [class.ring-2]="
              currentPlanIs('starter') ||
              (trialExpired && recommendedPlan === 'starter')
            "
            [class.ring-purple-500]="currentPlanIs('starter')"
            [class.ring-amber-400]="
              !currentPlanIs('starter') &&
              trialExpired &&
              recommendedPlan === 'starter'
            "
          >
            @if (currentPlanIs("starter")) {
              <div
                class="current-badge absolute -top-3 right-4 text-white text-xs font-bold px-3 py-1 rounded-full"
              >
                ACTUEL
              </div>
            } @else if (isPendingPlan("starter")) {
              <div
                class="absolute -top-3 right-4 bg-orange-400 text-orange-900 text-xs font-bold px-3 py-1 rounded-full"
              >
                📅 PROGRAMMÉ
              </div>
            } @else if (trialExpired && recommendedPlan === "starter") {
              <div
                class="absolute -top-3 right-4 bg-amber-400 text-amber-900 text-xs font-bold px-3 py-1 rounded-full"
              >
                💡 RECOMMANDÉ
              </div>
            }
            <div class="mb-6">
              <h3 class="text-lg font-bold text-gray-900 dark:text-white">
                Starter
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Parfait pour débuter
              </p>
            </div>
            <div class="mb-6">
              <span
                class="text-4xl font-extrabold text-gray-900 dark:text-white"
                >{{ priceLabel("starter") }}</span
              >
              <span class="text-gray-500 dark:text-gray-400">{{
                priceUnit
              }}</span>
              @if (annualHint("starter")) {
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {{ annualHint("starter") }}
                </p>
              }
              @if (founderHint("starter")) {
                <p class="text-xs font-semibold text-amber-600 mt-1">
                  {{ founderHint("starter") }}
                </p>
              }
            </div>
            <ul
              class="space-y-3 text-sm text-gray-700 dark:text-gray-300 flex-grow"
            >
              <li class="flex items-center gap-2.5">
                <span class="check-icon">✓</span> 10 événements / mois (marchand
                itinérant)
              </li>
              <li class="flex items-center gap-2.5">
                <span class="check-icon">✓</span> Recommandations intelligentes
              </li>
              <li class="flex items-center gap-2.5">
                <span class="check-icon">✓</span> Météo temps réel
              </li>
              <li class="flex items-center gap-2.5">
                <span class="check-icon">✓</span> Export PDF
              </li>
              <li class="flex items-center gap-2.5">
                <span class="check-icon">✓</span> Support par email
              </li>
            </ul>
            @if (currentPlanIs("starter")) {
              <button
                disabled
                class="mt-8 w-full py-3 rounded-xl bg-gray-100 dark:bg-slate-800 text-gray-400 font-bold text-sm cursor-default"
              >
                Plan actuel
              </button>
              @if (
                subscription()?.endDate && !subscription()?.cancelAtPeriodEnd
              ) {
                <p class="mt-2 text-center text-xs text-gray-400">
                  {{
                    hasPendingDowngrade
                      ? "✅ Actif jusqu'au"
                      : "🔄 Renouvellement le"
                  }}
                  {{ subscription()?.endDate | date: "longDate" }}
                </p>
              }
            } @else if (isPendingPlan("starter")) {
              <button
                disabled
                class="mt-8 w-full py-3 rounded-xl bg-orange-50 dark:bg-orange-900/20 text-orange-600 border border-orange-200 dark:border-orange-800 font-bold text-sm cursor-default"
              >
                📅 Programmé
              </button>
              <p class="mt-2 text-center text-xs text-orange-500">
                À partir du
                {{
                  subscription()?.pendingPlanEffectiveDate | date: "longDate"
                }}
              </p>
            } @else {
              <button
                (click)="selectPlan('starter')"
                class="mt-8 w-full py-3 rounded-xl font-bold text-sm transition btn-primary"
              >
                Passer à Starter
              </button>
            }
          </div>

          <!-- ── Pro (featured) ───────────────────────────────────────────── -->
          <div
            class="pricing-card pricing-featured rounded-2xl p-7 text-white flex flex-col relative"
            [class.ring-2]="currentPlanIs('pro')"
            [class.ring-yellow-400]="currentPlanIs('pro')"
          >
            <div class="absolute -top-3.5 left-1/2 -translate-x-1/2">
              @if (currentPlanIs("pro")) {
                <span
                  class="current-badge text-white text-xs font-bold px-3 py-1 rounded-full"
                >
                  ACTUEL
                </span>
              } @else if (isPendingPlan("pro")) {
                <span
                  class="bg-orange-400 text-orange-900 text-xs font-bold px-3 py-1 rounded-full"
                >
                  📅 PROGRAMMÉ
                </span>
              } @else {
                <span
                  class="bg-yellow-400 text-yellow-900 text-xs font-extrabold px-4 py-1.5 rounded-full shadow"
                >
                  ⭐ POPULAIRE
                </span>
              }
            </div>
            <div class="mb-6">
              <h3 class="text-lg font-bold">Pro</h3>
              <p class="text-sm text-purple-200 mt-1">Pour les pros actifs</p>
            </div>
            <div class="mb-6">
              <span class="text-4xl font-extrabold">{{
                priceLabel("pro")
              }}</span>
              <span class="text-purple-200">{{ priceUnit }}</span>
              @if (annualHint("pro")) {
                <p class="text-xs text-purple-200 mt-1">
                  {{ annualHint("pro") }}
                </p>
              }
              @if (founderHint("pro")) {
                <p class="text-xs font-semibold text-yellow-300 mt-1">
                  {{ founderHint("pro") }}
                </p>
              }
            </div>
            <ul class="space-y-3 text-sm flex-grow">
              <li class="flex items-center gap-2.5">
                <span class="check-icon-light">✓</span> Événements illimités
                (marchand itinérant)
              </li>
              <li class="flex items-center gap-2.5">
                <span class="check-icon-light">✓</span> Tout Starter +
              </li>
              <li class="flex items-center gap-2.5">
                <span class="check-icon-light">✓</span> Statistiques avancées
              </li>
              <li class="flex items-center gap-2.5">
                <span class="check-icon-light">✓</span> Alertes automatiques
              </li>
              @if (isFixedMerchant) {
                <li class="flex items-center gap-2.5">
                  <span class="check-icon-light">✓</span> Checklist de
                  préparation
                </li>
              }
              @if (isItinerantMerchant) {
                <li class="flex items-center gap-2.5">
                  <span class="check-icon-light">✓</span> Zone d'activité
                </li>
                <li class="flex items-center gap-2.5">
                  <span class="check-icon-light">✓</span> Opportunités
                  recommandées
                </li>
              }
              <li class="flex items-center gap-2.5">
                <span class="check-icon-light">✓</span> Support prioritaire
              </li>
            </ul>
            @if (currentPlanIs("pro")) {
              <button
                disabled
                class="mt-8 w-full py-3 rounded-xl bg-white/20 text-white font-bold text-sm cursor-default"
              >
                Plan actuel
              </button>
              @if (
                subscription()?.endDate && !subscription()?.cancelAtPeriodEnd
              ) {
                <p class="mt-2 text-center text-xs text-purple-200">
                  {{
                    hasPendingDowngrade
                      ? "✅ Actif jusqu'au"
                      : "🔄 Renouvellement le"
                  }}
                  {{ subscription()?.endDate | date: "longDate" }}
                </p>
              }
            } @else if (isPendingPlan("pro")) {
              <button
                disabled
                class="mt-8 w-full py-3 rounded-xl bg-orange-50 dark:bg-orange-900/20/20 text-orange-200 border border-orange-300/30 font-bold text-sm cursor-default"
              >
                📅 Programmé
              </button>
              <p class="mt-2 text-center text-xs text-orange-200">
                À partir du
                {{
                  subscription()?.pendingPlanEffectiveDate | date: "longDate"
                }}
              </p>
            } @else {
              <button
                (click)="selectPlan('pro')"
                class="mt-8 w-full py-3 rounded-xl font-bold text-sm btn-white"
              >
                Passer à Pro
              </button>
            }
          </div>

          <!-- ── Business ─────────────────────────────────────────────────── -->
          <div
            class="pricing-card bg-white dark:bg-slate-800 rounded-2xl p-7 border border-gray-200 dark:border-slate-700 flex flex-col"
            [class.ring-2]="currentPlanIs('business')"
            [class.ring-purple-500]="currentPlanIs('business')"
          >
            @if (currentPlanIs("business")) {
              <div
                class="current-badge absolute -top-3 right-4 text-white text-xs font-bold px-3 py-1 rounded-full"
              >
                ACTUEL
              </div>
            }
            @if (isPendingPlan("business")) {
              <div
                class="absolute -top-3 right-4 bg-orange-400 text-orange-900 text-xs font-bold px-3 py-1 rounded-full"
              >
                📅 PROGRAMMÉ
              </div>
            }
            <div class="mb-6">
              <h3 class="text-lg font-bold text-gray-900 dark:text-white">
                Business
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Multi-stands & équipes
              </p>
            </div>
            <div class="mb-6">
              <span
                class="text-4xl font-extrabold text-gray-900 dark:text-white"
                >{{ priceLabel("business") }}</span
              >
              <span class="text-gray-500 dark:text-gray-400">{{
                priceUnit
              }}</span>
              @if (annualHint("business")) {
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {{ annualHint("business") }}
                </p>
              }
              @if (founderHint("business")) {
                <p class="text-xs font-semibold text-amber-600 mt-1">
                  {{ founderHint("business") }}
                </p>
              }
            </div>
            <ul
              class="space-y-3 text-sm text-gray-700 dark:text-gray-300 flex-grow"
            >
              <li class="flex items-center gap-2.5">
                <span class="check-icon">✓</span> Tout Pro +
              </li>
              <li class="flex items-center gap-2.5">
                <span class="check-icon">✓</span> Jusqu'à 5 stands
              </li>
              <li class="flex items-center gap-2.5">
                <span class="check-icon">✓</span> Gestion d'équipe
              </li>
              <li class="flex items-center gap-2.5">
                <span class="check-icon">✓</span> Comparaison avec votre secteur
              </li>
              <li class="flex items-center gap-2.5">
                <span class="check-icon">✓</span> Accompagnement personnalisé
              </li>
            </ul>
            @if (currentPlanIs("business")) {
              <button
                disabled
                class="mt-8 w-full py-3 rounded-xl bg-gray-100 dark:bg-slate-800 text-gray-400 font-bold text-sm cursor-default"
              >
                Plan actuel
              </button>
              @if (
                subscription()?.endDate && !subscription()?.cancelAtPeriodEnd
              ) {
                <p class="mt-2 text-center text-xs text-gray-400">
                  {{
                    hasPendingDowngrade
                      ? "✅ Actif jusqu'au"
                      : "🔄 Renouvellement le"
                  }}
                  {{ subscription()?.endDate | date: "longDate" }}
                </p>
              }
            } @else if (isPendingPlan("business")) {
              <button
                disabled
                class="mt-8 w-full py-3 rounded-xl bg-orange-50 dark:bg-orange-900/20 text-orange-600 border border-orange-200 dark:border-orange-800 font-bold text-sm cursor-default"
              >
                📅 Programmé
              </button>
              <p class="mt-2 text-center text-xs text-orange-500">
                À partir du
                {{
                  subscription()?.pendingPlanEffectiveDate | date: "longDate"
                }}
              </p>
            } @else {
              <button
                disabled
                class="mt-8 w-full py-3 rounded-xl font-bold text-sm bg-gray-100 dark:bg-slate-800 text-gray-400 cursor-default"
              >
                Bientôt disponible
              </button>
            }
          </div>
        </div>

        <!-- Trust badges -->
        <div class="trust-row mt-10">
          <span class="trust-badge">🔒 Paiement sécurisé</span>
          <span class="trust-badge">🚫 Sans engagement</span>
          <span class="trust-badge">⚡ Activation immédiate</span>
          <span class="trust-badge">💬 Support réactif</span>
        </div>
      </div>

      <!-- ═══ FAQ section (mini) ════════════════════════════════════════════ -->
      <div class="max-w-2xl mx-auto mb-10">
        <h3
          class="text-lg font-bold text-gray-900 dark:text-white text-center mb-6"
        >
          Questions fréquentes
        </h3>
        <div class="space-y-3">
          <details
            class="group bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
          >
            <summary
              class="cursor-pointer px-5 py-4 font-medium text-gray-800 dark:text-gray-100 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition"
            >
              Puis-je changer de plan à tout moment ?
              <span
                class="text-gray-400 group-open:rotate-180 transition-transform"
                >▾</span
              >
            </summary>
            <p class="px-5 pb-4 text-sm text-gray-600 dark:text-gray-400">
              Oui, vous pouvez passer à un plan supérieur ou inférieur à tout
              moment. Le changement est immédiat et la différence de prix est
              calculée au prorata.
            </p>
          </details>
          <details
            class="group bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
          >
            <summary
              class="cursor-pointer px-5 py-4 font-medium text-gray-800 dark:text-gray-100 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition"
            >
              Que se passe-t-il à la fin de mon essai ?
              <span
                class="text-gray-400 group-open:rotate-180 transition-transform"
                >▾</span
              >
            </summary>
            <p class="px-5 pb-4 text-sm text-gray-600 dark:text-gray-400">
              À la fin du mois d'essai gratuit (non renouvelable), vous devrez
              choisir un plan payant pour continuer à utiliser Predivent. Vos
              données sont conservées.
            </p>
          </details>
          <details
            class="group bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
          >
            <summary
              class="cursor-pointer px-5 py-4 font-medium text-gray-800 dark:text-gray-100 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition"
            >
              Puis-je mettre mon abonnement en pause ?
              <span
                class="text-gray-400 group-open:rotate-180 transition-transform"
                >▾</span
              >
            </summary>
            <p class="px-5 pb-4 text-sm text-gray-600 dark:text-gray-400">
              Oui, une fois abonné (la pause ne s'applique pas à l'essai
              gratuit). Elle prend effet à la fin de la période déjà payée, sans
              nouveau prélèvement. Votre historique et vos données sont
              conservés. La reprise est payante et ne donne pas droit à un
              nouvel essai gratuit.
            </p>
          </details>
          <details
            class="group bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
          >
            <summary
              class="cursor-pointer px-5 py-4 font-medium text-gray-800 dark:text-gray-100 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition"
            >
              Comment annuler mon abonnement ?
              <span
                class="text-gray-400 group-open:rotate-180 transition-transform"
                >▾</span
              >
            </summary>
            <p class="px-5 pb-4 text-sm text-gray-600 dark:text-gray-400">
              Depuis cette page, cliquez sur « Annuler ». Votre accès sera
              maintenu jusqu'à la fin de la période déjà payée.
            </p>
          </details>
        </div>
      </div>

      <!-- Back to dashboard link -->
      @if (!trialExpired) {
        <div class="text-center">
          <a
            routerLink="/dashboard"
            class="text-sm text-purple-600 hover:text-purple-800 dark:text-purple-300 font-medium hover:underline transition"
          >
            ← Retour au dashboard
          </a>
        </div>
      }
    </div>
  </div>
}
<!-- end @if (!paymentLoading) -->

<!-- ═══ MODALE : Confirmation Changement de Plan ══════════════════════════ -->
@if (showChangePlanModal) {
  <div
    class="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-4"
  >
    <div
      class="modal-content bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full p-7 shadow-2xl"
    >
      <div
        class="w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto"
        [class]="
          isUpgrade(targetPlan)
            ? 'bg-purple-100 dark:bg-purple-900/30'
            : 'bg-amber-100 dark:bg-amber-900/30'
        "
      >
        <span class="text-2xl">{{ isUpgrade(targetPlan) ? "🚀" : "📉" }}</span>
      </div>
      <h3
        class="text-xl font-bold text-gray-900 dark:text-white text-center mb-4"
      >
        {{
          isUpgrade(targetPlan)
            ? "Confirmer l'upgrade"
            : "Confirmer la rétrogradation"
        }}
      </h3>

      <!-- Récapitulatif ancien → nouveau plan -->
      <div class="bg-gray-50 dark:bg-slate-900 rounded-xl p-4 mb-4 space-y-3">
        <div class="flex items-center justify-between">
          <div class="text-center flex-1">
            <p
              class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide"
            >
              Ancien plan
            </p>
            <p
              class="text-lg font-bold text-gray-700 dark:text-gray-300 mt-0.5"
            >
              {{ currentPlanLabel }}
            </p>
            <p class="text-xs text-gray-400">
              {{ priceLabel(subscription()?.plan || "free") }}{{ priceUnit }}
            </p>
          </div>
          <div class="mx-3 text-gray-300 text-xl">→</div>
          <div class="text-center flex-1">
            <p
              class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide"
            >
              Nouveau plan
            </p>
            <p
              class="text-lg font-bold mt-0.5"
              [class]="
                isUpgrade(targetPlan)
                  ? 'text-purple-700 dark:text-purple-300'
                  : 'text-amber-700 dark:text-amber-300'
              "
            >
              {{ targetPlanLabel }}
            </p>
            <p class="text-xs text-gray-400">
              {{ priceLabel(targetPlan) }}{{ priceUnit }}
            </p>
          </div>
        </div>

        <!-- Dates souscription & renouvellement -->
        @if (subscription()?.startDate) {
          <div
            class="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-1.5 text-sm text-gray-600 dark:text-gray-400"
          >
            <div class="flex justify-between">
              <span>📅 Date de souscription</span>
              <span class="font-semibold text-gray-800 dark:text-gray-100">{{
                subscription()?.startDate | date: "longDate"
              }}</span>
            </div>
            @if (
              subscription()?.endDate && !subscription()?.cancelAtPeriodEnd
            ) {
              <div class="flex justify-between">
                <span>🔄 Prochain renouvellement</span>
                <span class="font-semibold text-gray-800 dark:text-gray-100">{{
                  subscription()?.endDate | date: "longDate"
                }}</span>
              </div>
            }
          </div>
        }
      </div>

      @if (isUpgrade(targetPlan)) {
        <p class="text-gray-600 dark:text-gray-400 text-center text-sm mb-6">
          La différence sera facturée au prorata et vous aurez accès aux
          nouvelles fonctionnalités immédiatement.
        </p>
      } @else {
        <p class="text-gray-600 dark:text-gray-400 text-center text-sm mb-6">
          Vous conserverez l'accès aux fonctionnalités avancées jusqu'à la fin
          de votre période en cours. Le nouveau tarif s'appliquera au prochain
          renouvellement.
        </p>
      }
      <div class="flex gap-3">
        <button
          (click)="showChangePlanModal = false"
          class="flex-1 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 rounded-xl font-medium transition"
        >
          Annuler
        </button>
        <button
          (click)="confirmPlanChange()"
          [disabled]="isPortalLoading"
          class="flex-1 py-3 btn-primary rounded-xl font-bold flex items-center justify-center gap-2"
        >
          @if (isPortalLoading) {
            <span class="spinner"></span>
          }
          Confirmer
        </button>
      </div>
    </div>
  </div>
}

<!-- ═══ MODALE : Annulation (Étape 1 : Sondage) ══════════════════════════ -->
@if (showCancelModal) {
  <div
    class="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-4"
  >
    <div
      class="modal-content bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full p-7 shadow-2xl"
    >
      <div
        class="w-12 h-12 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4 mx-auto"
      >
        <span class="text-2xl">😢</span>
      </div>
      <h3
        class="text-xl font-bold text-gray-900 dark:text-white text-center mb-1"
      >
        Nous sommes tristes de vous voir partir
      </h3>
      <p class="text-gray-500 dark:text-gray-400 text-center text-sm mb-5">
        Pourquoi souhaitez-vous annuler ?
      </p>
      <div class="space-y-2 mb-6">
        @for (reason of cancelReasons; track reason.value) {
          <label
            class="flex items-center p-3 border rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition"
            [class.border-purple-300]="selectedCancelReason === reason.value"
            [class.bg-purple-50]="selectedCancelReason === reason.value"
          >
            <input
              type="radio"
              name="cancelReason"
              [value]="reason.value"
              [(ngModel)]="selectedCancelReason"
              class="accent-purple-600"
            />
            <span class="ml-3 text-gray-700 dark:text-gray-300 text-sm">{{
              reason.label
            }}</span>
          </label>
        }
      </div>
      <div class="flex gap-3">
        <button
          (click)="showCancelModal = false"
          class="flex-1 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 rounded-xl font-medium transition"
        >
          Retour
        </button>
        <button
          (click)="proceedToCancelConfirmation()"
          [disabled]="!selectedCancelReason"
          class="flex-1 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          Continuer
        </button>
      </div>
    </div>
  </div>
}

<!-- ═══ MODALE : Annulation (Étape 2 : Confirmation finale) ═══════════════ -->
@if (showFinalCancelModal) {
  <div
    class="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-4"
  >
    <div
      class="modal-content bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full p-7 shadow-2xl"
    >
      <div
        class="w-14 h-14 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mb-4 mx-auto"
      >
        <svg
          class="w-7 h-7 text-yellow-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h3
        class="text-xl font-bold text-gray-900 dark:text-white text-center mb-2"
      >
        Êtes-vous vraiment sûr ?
      </h3>
      <p class="text-gray-600 dark:text-gray-400 text-center text-sm mb-4">
        Votre abonnement restera actif jusqu'au
        <span class="font-bold">{{
          subscription()?.endDate | date: "longDate"
        }}</span
        >. Après cette date,
        <span class="font-semibold text-red-600 dark:text-red-400"
          >vous perdrez l'accès à Predivent</span
        >.
      </p>
      <p class="text-gray-500 dark:text-gray-400 text-center text-xs mb-6">
        Vous pourrez toujours revenir et souscrire un plan payant
        ultérieurement.
      </p>
      <div class="flex flex-col gap-3">
        <button
          (click)="showFinalCancelModal = false; showCancelModal = false"
          class="w-full py-3 btn-primary rounded-xl font-bold"
        >
          Non, je garde mon abonnement
        </button>
        <button
          (click)="confirmCancellation()"
          [disabled]="isPortalLoading"
          class="w-full py-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-xl font-semibold hover:bg-red-100 dark:bg-red-900/30 flex justify-center items-center gap-2 transition"
        >
          @if (isPortalLoading) {
            <span class="spinner"></span>
          }
          Confirmer l'annulation
        </button>
      </div>
    </div>
  </div>
}

<!-- ═══ MODALE : Mise en pause de l'abonnement ═══════════════════════════ -->
@if (showPauseModal) {
  <div
    class="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-4"
  >
    <div
      class="modal-content bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full p-7 shadow-2xl"
    >
      <div
        class="w-12 h-12 bg-sky-100 dark:bg-sky-900/30 rounded-full flex items-center justify-center mb-4 mx-auto"
      >
        <span class="text-2xl">⏸️</span>
      </div>
      <h3
        class="text-xl font-bold text-gray-900 dark:text-white text-center mb-3"
      >
        Mettre mon abonnement en pause
      </h3>
      <ul
        class="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-5 list-disc pl-5"
      >
        <li>
          Accès complet jusqu'au
          <span class="font-semibold">{{
            subscription()?.endDate | date: "longDate"
          }}</span
          >, puis plus aucun prélèvement ni facture.
        </li>
        <li>Votre historique et vos données personnelles sont conservés.</li>
        <li>
          Reprise possible à tout moment, mais payante : aucune nouvelle période
          d'essai gratuite.
        </li>
      </ul>
      <div class="flex gap-3">
        <button
          (click)="showPauseModal = false"
          class="flex-1 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 rounded-xl font-medium transition"
        >
          Retour
        </button>
        <button
          (click)="confirmPause()"
          [disabled]="isPortalLoading"
          class="flex-1 py-3 btn-primary rounded-xl font-bold flex items-center justify-center gap-2"
        >
          @if (isPortalLoading) {
            <span class="spinner"></span>
          }
          Confirmer la pause
        </button>
      </div>
    </div>
  </div>
}
